import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  input,
  output,
} from '@angular/core';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { IconComponent } from '../icon/icon.component';

/**
 * A search box for a page HEADER, where the design puts the search beside the view switcher rather than
 * inside the card below it.
 *
 * The portlet has a search of its own for a card that owns its toolbar; this is the standalone one, for a
 * page whose card has no toolbar to put it in. Same anatomy either way — magnifier, placeholder, a `/`
 * hint chip and a clear button — because a reader who has learnt one should recognise the other.
 *
 * `/` from anywhere on the page focuses it (unless the reader is already typing somewhere), and `Escape`
 * clears it and steps out. The shortcut belongs to the field, not to every host that renders one.
 */
@Component({
  selector: 'app-search-field',
  standalone: true,
  imports: [IconComponent, NgbTooltipModule],
  template: `
    <div class="sf" [class.has-value]="!!value()">
      <app-icon name="search" [size]="16" class="sf-icon" />
      <input
        #field
        type="text"
        class="sf-input"
        [placeholder]="placeholder()"
        [value]="value()"
        [attr.aria-label]="placeholder()"
        ngbTooltip="Press / to search"
        (input)="valueChange.emit($any($event.target).value)"
        (keydown.escape)="clear()"
      />
      @if (!value()) {
        <kbd class="sf-kbd" aria-hidden="true">/</kbd>
      }
      <button
        type="button"
        class="sf-clear"
        [hidden]="!value()"
        ngbTooltip="Clear search"
        aria-label="Clear search"
        (click)="clear()"
      >
        <app-icon name="x" [size]="16" />
      </button>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .sf {
        display: flex;
        align-items: center;
        gap: 8px;
        height: 38px;
        padding: 0 12px;
        border: 1px solid var(--border-default, #e5e7eb);
        border-radius: 999px;
        background: var(--bg-card, #fff);
        transition:
          border-color 0.15s ease,
          box-shadow 0.15s ease;
      }

      .sf:focus-within {
        border-color: var(--h4c-bright-blue, #27aae1);
        box-shadow: 0 0 0 3px var(--h4c-bright-blue-14, rgba(39, 170, 225, 0.14));
      }

      .sf-icon {
        color: var(--fg-3, #94a3b8);
        flex-shrink: 0;
      }

      .sf-input {
        border: none;
        outline: none;
        background: transparent;
        width: 100%;
        min-width: 0;
        font-size: 0.8125rem;
        color: var(--fg-1, #334155);
      }

      .sf-input::placeholder {
        color: var(--fg-3, #94a3b8);
      }

      /* The chip is a hint, so it steps aside the moment the field is actually in use. */
      .sf-kbd {
        flex-shrink: 0;
        min-width: 18px;
        padding: 1px 6px;
        border: 1px solid var(--border-default, #e5e7eb);
        border-radius: 5px;
        background: var(--bg-subtle, #f3f4f6);
        color: var(--fg-3, #94a3b8);
        font-size: 0.6875rem;
        font-weight: 600;
        line-height: 16px;
        text-align: center;
      }

      .sf:focus-within .sf-kbd {
        display: none;
      }

      .sf-clear {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        padding: 0;
        border: none;
        border-radius: var(--radius-md, 8px);
        background: transparent;
        color: var(--fg-3, #94a3b8);
        cursor: pointer;
        transition: all 0.15s ease;
      }

      .sf-clear:hover {
        background: var(--bg-subtle, #f3f4f6);
        color: var(--color-danger, #ef4444);
      }

      @media (prefers-reduced-motion: reduce) {
        .sf,
        .sf-clear {
          transition: none;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchFieldComponent {
  readonly value = input('');
  readonly placeholder = input('Search');

  readonly valueChange = output<string>();

  @ViewChild('field') private readonly field?: ElementRef<HTMLInputElement>;

  /**
   * `/` focuses the field from anywhere on the page — except while the reader is already typing, where
   * it is a character they meant.
   */
  @HostListener('document:keydown', ['$event'])
  protected onDocumentKey(event: KeyboardEvent): void {
    if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;

    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
      return;
    }

    event.preventDefault();
    this.focus();
  }

  focus(): void {
    this.field?.nativeElement.focus();
  }

  protected clear(): void {
    this.valueChange.emit('');
    this.field?.nativeElement.blur();
  }
}
